import { z } from 'zod';

import {
  type BettingSplitsBet,
  type BettingSplitsESResult,
} from '~/apps/product-plus/search-domain/pipelines/betting-splits/types';
import { type MatchupCentreESResult } from '~/apps/product-plus/search-domain/pipelines/match-centre/types';
import { type SubeventTeamData } from '~/apps/product-plus/search-domain/types/betting-insight';

import { schemaTeam } from './extractSubevent';

const schemaBet = z.object({
  betCount: z.number(),
  betId: z.number(),
  betName: z.string(),
  betPercentage: z.number(),
  bookmakerCode: z.string(),
  line: z
    .object({
      absolute: z.string(),
      name: z.string(),
      sign: z.string(),
    })
    .nullable()
    .optional(),
  oddsAmerican: z.string(),
  oddsDecimal: z.number(),
  oddsFractional: z.string(),
  parentBetId: z.number(),
  stakeBetDiff: z.number(),
  stakePercentage: z.number(),
  teamData: z.object({
    away: schemaTeam,
    home: schemaTeam,
  }),
});

const schemaOddsBet = schemaBet.extend({
  betCount: z.number().optional(),
  betPercentage: z.number().optional(),
  stakeBetDiff: z.number().optional(),
  stakePercentage: z.number().optional(),
});

export function extractBet<T extends boolean = true>(
  result: BettingSplitsESResult,
  strict: T = true as T,
): T extends true
  ? BettingSplitsBet
  : Omit<BettingSplitsBet, 'betCount' | 'betPercentage' | 'stakeBetDiff' | 'stakePercentage'> {
  const schema = strict ? schemaBet : schemaOddsBet;
  const parsedBet = schema.safeParse(result);

  if (!parsedBet.success) {
    throw new Error(JSON.stringify(parsedBet.error.flatten().fieldErrors));
  }

  const {
    betCount,
    betId,
    betName,
    betPercentage,
    bookmakerCode,
    line,
    oddsAmerican,
    oddsDecimal,
    oddsFractional,
    parentBetId,
    stakeBetDiff,
    stakePercentage,
    teamData,
  } = parsedBet.data;

  const teamType = Object.entries(teamData ?? {})
    .filter(([_, team]) => team.name === (line ? line.name : betName))
    .map(([key]) => key as keyof SubeventTeamData)
    .pop();

  return {
    ...(strict
      ? {
          betCount,
          betPercentage,
          stakeBetDiff,
          stakePercentage,
        }
      : {}),
    betId,
    betName,
    bookmakerCode,
    line,
    oddsAmerican,
    oddsDecimal,
    oddsFractional,
    parentBetId,
    ...(teamType ? { teamType } : {}),
  } as T extends true
    ? BettingSplitsBet
    : Omit<BettingSplitsBet, 'betCount' | 'betPercentage' | 'stakeBetDiff' | 'stakePercentage'>;
}

const schemaMatchupCentreBet = schemaBet.omit({ teamData: true }).extend({
  betCount: z.number().nullable().optional(),
  betPercentage: z.number().nullable().optional(),
  expectedValue: z.number().nullable(),
  expectedValueStatus: z.string().optional(),
  stakeBetDiff: z.number().nullable().optional(),
  stakePercentage: z.number().nullable().optional(),
  teamData: z
    .object({
      away: schemaTeam,
      home: schemaTeam,
    })
    .nullable(),
});

export type MatchupCentreBet = Omit<
  BettingSplitsBet,
  'betCount' | 'betPercentage' | 'stakeBetDiff' | 'stakePercentage' | 'teamType'
> & {
  betCount?: number;
  betPercentage?: number;
  expectedValue: number | null;
  expectedValueStatus?: string;
  stakeBetDiff?: number;
  stakePercentage?: number;
  teamType?: keyof SubeventTeamData;
};

/**
 * Helper function to include a field only if it has a valid numeric value.
 * Filters out null and undefined values.
 */
function includeIfPresent<T extends Record<string, unknown>>(
  key: keyof T,
  value: number | null | undefined,
): Partial<T> {
  return value !== undefined && value !== null ? ({ [key]: value } as Partial<T>) : {};
}

export function extractMatchupCentreBet(result: MatchupCentreESResult): MatchupCentreBet {
  const parsedBet = schemaMatchupCentreBet.safeParse(result);

  if (!parsedBet.success) {
    throw new Error(JSON.stringify(parsedBet.error.flatten().fieldErrors));
  }

  const {
    betCount,
    betId,
    betName,
    betPercentage,
    bookmakerCode,
    expectedValue,
    expectedValueStatus,
    line,
    oddsAmerican,
    oddsDecimal,
    oddsFractional,
    parentBetId,
    stakeBetDiff,
    stakePercentage,
    teamData,
  } = parsedBet.data;

  const teamType = Object.entries(teamData ?? {})
    .filter(([_, team]) => team.name === (line ? line.name : betName))
    .map(([key]) => key as keyof SubeventTeamData)
    .pop();

  return {
    ...includeIfPresent<MatchupCentreBet>('betCount', betCount),
    ...includeIfPresent<MatchupCentreBet>('betPercentage', betPercentage),
    ...includeIfPresent<MatchupCentreBet>('stakeBetDiff', stakeBetDiff),
    ...includeIfPresent<MatchupCentreBet>('stakePercentage', stakePercentage),
    betId,
    betName,
    bookmakerCode,
    expectedValue,
    ...(expectedValueStatus ? { expectedValueStatus } : {}),
    line: line || undefined,
    oddsAmerican,
    oddsDecimal,
    oddsFractional,
    parentBetId,
    ...(teamType ? { teamType } : {}),
  };
}
